// App
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useYieldCallback } from '@zillow/yield-callback';

// Components
import AllFiltersButton from 'app/shared/modules/filter/FilterBar/AllFiltersButton';
import PriceFilterButton from 'app/shared/modules/filter/FilterBar/PriceFilterButton';
import BedBathFilterButton from 'app/shared/modules/filter/FilterBar/BedBathFilterButton';
import CommuteFilterButton from 'app/shared/modules/filter/FilterBar/CommuteFilterButton';
import PetFilterButton from 'app/shared/modules/filter/FilterBar/PetFilterButton';
import ClearAllFiltersButton from 'app/shared/modules/filter/FilterBar/ClearAllFiltersButton';

// Hooks
import { useFilterChange } from 'app/shared/modules/filter/FilterBar/hooks';

// Misc / utils
import { filterUtils_hasActiveFilters } from 'app/shared/utils/filterUtils';
import * as S from './styles';
import type { RootReduxState } from 'app/shared/flux/reducers';

const INIT_STATE = {
  allFilters: false,
  priceFilter: false,
  bedBathFilter: false,
  commuteFilter: false,
  petsFilter: false,
};
const FilterBar = () => {
  // Redux
  const filter = useSelector((state: RootReduxState) => state.filter);

  // Hooks
  const { filterChange, shouldFetchFilteredResults } = useFilterChange();

  // React state
  const [showDropdown, setShowDropdown] = useState(INIT_STATE);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  useEffect(() => {
    setHasActiveFilters(filterUtils_hasActiveFilters(filter));
  }, [filter]);

  const yieldShowDropdown = useYieldCallback((option = {}) => {
    setShowDropdown({ ...INIT_STATE, ...option });
  });

  const handleAllFiltersButtonClick = () => {
    yieldShowDropdown({ allFilters: !showDropdown.allFilters });
  };
  const handlePriceFilterButtonClick = () => {
    yieldShowDropdown({ priceFilter: !showDropdown.priceFilter });
  };
  const handleBedBathButtonClick = () => yieldShowDropdown({ bedBathFilter: !showDropdown.bedBathFilter });
  const handleCommuteButtonClick = () => yieldShowDropdown({ commuteFilter: !showDropdown.commuteFilter });
  const handlePetButtonClick = () => yieldShowDropdown({ petsFilter: !showDropdown.petsFilter });

  const handleClose = async () => {
    // If filter has changed, fetch new listings and close dropdown
    if (shouldFetchFilteredResults()) {
      await filterChange({ ...filter });
      yieldShowDropdown();
    } else {
      // Else, just close dropdown
      yieldShowDropdown();
    }
  };

  return (
    <>
      <S.FilterBarSection>
        <AllFiltersButton
          ariaButtonLabel={'Show all filters'}
          ariaDropdownLabel={'List of all filters available to fine tune your rental search'}
          onButtonClick={handleAllFiltersButtonClick}
          onModalClose={handleClose}
          showDropdown={showDropdown.allFilters}
        />
        <PriceFilterButton
          ariaButtonLabel={'Show price filter'}
          ariaDropdownLabel={'Set your minimum and maximum price preferences and listings will be updated accordingly'}
          onButtonClick={handlePriceFilterButtonClick}
          onModalClose={handleClose}
          showDropdown={showDropdown.priceFilter}
        />
        <BedBathFilterButton
          ariaButtonLabel={'Show bedroom and bathroom filters'}
          ariaDropdownLabel={'Set your bedroom and bathroom preferences and listings will be updated accordingly'}
          onButtonClick={handleBedBathButtonClick}
          onModalClose={handleClose}
          showDropdown={showDropdown.bedBathFilter}
        />
        <CommuteFilterButton
          ariaButtonLabel={'Show daily commute filters'}
          ariaDropdownLabel={'Set your daily commute preferences and listings will be updated accordingly'}
          onButtonClick={handleCommuteButtonClick}
          onModalClose={handleClose}
          showDropdown={showDropdown.commuteFilter}
        />
        <PetFilterButton
          ariaButtonLabel={'Show pet friendly filters'}
          ariaDropdownLabel={'Set your pet filter preferences and listings will be updated accordingly'}
          onButtonClick={handlePetButtonClick}
          onModalClose={handleClose}
          showDropdown={showDropdown.petsFilter}
        />
        {hasActiveFilters && <ClearAllFiltersButton />}
      </S.FilterBarSection>
    </>
  );
};

export default FilterBar;
